"use client";

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.array.fill.js";
import "core-js/modules/es.array.is-array.js";
import "core-js/modules/es.array.map.js";
import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Row, Column, Flex } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import merge from "lodash/merge";
import React, { useState } from "react";
import styled, { css } from "styled-components";
import { IconButton } from "../IconButton";
import { Chevron } from "../icons/foundation/Chevron";
import { Close } from "../icons/foundation/Close";
import { Information } from "../icons/foundation/Information";
import { Spacer } from "../spacing";
import { themeOrDefault } from "../Theme";
import { HIGH_VIS_FILTER, highVisSlateComponent } from "../utils/highVisSlateComponent";
import { useTooltipOffsetToKeepTooltipInsideViewport } from "./__internal__/useTooltipOffsetToKeepTooltipInsideViewport";
import { TOOLTIP_POINTER_SIZE } from "./consts";
export var TooltipText = styled(Typography).attrs({
  color: "inherit"
}).withConfig({
  displayName: "common__TooltipText"
})(["", ";"], function (_ref) {
  var $oneLiner = _ref.$oneLiner;
  return $oneLiner ? css(["white-space:nowrap;"]) : "";
});
export var TOOLTIP_WRAPPER_Z_INDEX = 9;
export var TooltipWrapper = styled(Column).attrs({
  align: "flex-start"
}).withConfig({
  displayName: "common__TooltipWrapper"
})(["border:1px solid;color:", ";background-color:", ";border-color:", ";position:absolute;", ";", " z-index:", ";width:", ";", " max-width:80vw;", ""], function (_ref2) {
  var colors = _ref2.$colors;
  return colors.textColor;
}, function (_ref3) {
  var colors = _ref3.$colors;
  return colors.backgroundColor;
}, function (_ref4) {
  var colors = _ref4.$colors;
  return colors.borderColor;
}, function (_ref5) {
  var _ref5$$position = _ref5.$position,
    position = _ref5$$position === void 0 ? "top" : _ref5$$position,
    _ref5$$offset = _ref5.$offset,
    offset = _ref5$$offset === void 0 ? 0 : _ref5$$offset;
  switch (position) {
    case "top":
      return css(["top:0;left:50%;transform:translate(-50%,-100%) translateX(", "px) translateY(-15px);"], -offset);
    case "bottom":
      return css(["bottom:0;left:50%;transform:translate(-50%,100%) translateX(", "px) translateY(15px);"], -offset);
    case "left":
      return css(["top:50%;left:0;transform:translate(-100%,-50%) translateX(-15px) translateY(", "px);"], -offset);
    case "right":
    default:
      return css(["top:50%;right:0;transform:translate(100%,-50%) translateX(15px) translateY(", "px);"], -offset);
  }
}, function (_ref6) {
  var popout = _ref6.$popout;
  return popout ? css(["", " ", " ", " ", ""], popout.top ? css(["top:", ";"], popout.top) : "", popout.bottom ? css(["bottom:", ";"], popout.bottom) : "", popout.left ? css(["left:", ";"], popout.left) : "", popout.right ? css(["right:", ";"], popout.right) : "") : "";
}, TOOLTIP_WRAPPER_Z_INDEX, function (_ref7) {
  var width = _ref7.$width;
  return width === "auto" ? width : "".concat(width, "px");
}, function (_ref8) {
  var tiny = _ref8.$tiny,
    tinyRightPadding = _ref8.$tinyRightPadding;
  if (tiny) {
    return css(["padding:11px;"]);
  } else {
    return tinyRightPadding ? css(["padding:23px 11px 0 23px;"]) : css(["padding:23px 23px 0 23px;"]);
  }
}, highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT));
export var OuterWrapper = styled.span.withConfig({
  displayName: "common__OuterWrapper"
})(["display:inline-flex;align-items:center;", " ", "{display:", ";}", ""], function (_ref9) {
  var $popout = _ref9.$popout;
  return $popout ? "" : "position: relative;";
}, TooltipWrapper, function (_ref10) {
  var $isOpen = _ref10.$isOpen,
    $hidden = _ref10.$hidden;
  return $isOpen && !$hidden ? "flex" : "none";
}, function (_ref11) {
  var $dismissible = _ref11.$dismissible,
    $hidden = _ref11.$hidden;
  return $dismissible || $hidden ? "" : css(["&:hover{", "{display:flex;}}&:focus{", "{display:flex;}}&:focus-within{", "{display:flex;}}"], TooltipWrapper, TooltipWrapper, TooltipWrapper);
});
export var TOOLTIP_TIP_Z_INDEX = -100;
export var Tip = styled.div.withConfig({
  displayName: "common__Tip"
})(["background:inherit;position:absolute;z-index:", ";display:block;width:", "px;height:", "px;", ";border:inherit;border-top-color:transparent;border-left-color:transparent;"], TOOLTIP_TIP_Z_INDEX, TOOLTIP_POINTER_SIZE, TOOLTIP_POINTER_SIZE, function (_ref12) {
  var $position = _ref12.$position,
    _ref12$$offset = _ref12.$offset,
    $offset = _ref12$$offset === void 0 ? 0 : _ref12$$offset;
  switch ($position) {
    case "top":
      return css(["left:calc(50% + ", "px);bottom:0;transform:translate(-50%,50%) rotate(45deg);"], $offset);
    case "bottom":
      return css(["left:calc(50% + ", "px);top:0;transform:translate(-50%,-50%) rotate(225deg);"], $offset);
    case "left":
      return css(["top:calc(50% + ", "px);right:0;transform:translate(50%,-50%) rotate(315deg);"], $offset);
    case "right":
    default:
      return css(["top:calc(50% + ", "px);left:0;transform:translate(-50%,-50%) rotate(135deg);"], $offset);
  }
});
export var SpaceBetweenIconAndTooltip = styled.div.withConfig({
  displayName: "common__SpaceBetweenIconAndTooltip"
})(["position:absolute;", ";"], function (_ref13) {
  var $position = _ref13.$position;
  switch ($position) {
    case "top":
      return css(["left:0;bottom:-16px;height:16px;width:100%;"]);
    case "bottom":
      return css(["left:0;top:-16px;height:16px;width:100%;"]);
    case "left":
      return css(["top:0;right:-16px;height:100%;width:16px;"]);
    case "right":
    default:
      return css(["top:0;left:-16px;height:100%;width:16px;"]);
  }
});
export var ActionButtons = function ActionButtons(_ref14) {
  var _colors;
  var theme = _ref14.theme,
    current = _ref14.current,
    total = _ref14.total,
    onPrev = _ref14.onPrev,
    onNext = _ref14.onNext,
    colors = _ref14.colors,
    _ref14$variant = _ref14.variant,
    variant = _ref14$variant === void 0 ? "dark" : _ref14$variant,
    _ref14$autoFocusNext = _ref14.autoFocusNext,
    autoFocusNext = _ref14$autoFocusNext === void 0 ? false : _ref14$autoFocusNext,
    dataTestId = _ref14["data-test-id"];
  colors = mergeTooltipColors(theme, variant, colors);
  var _useStrictLocalizatio = useStrictLocalization(),
    strictLocalize = _useStrictLocalizatio.strictLocalize;
  return /*#__PURE__*/React.createElement(Row, {
    align: "center",
    justify: "space-between",
    style: {
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraphSansRegular",
    color: (_colors = colors) === null || _colors === void 0 ? void 0 : _colors.textColor
  }, current + 1, "/", total), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(StyledIconButton, {
    "aria-label": strictLocalize({
      id: "p1TqDF",
      defaultMessage: [{
        "type": 0,
        "value": "Vorige tip"
      }]
    }),
    className: "override",
    "data-test-id": dataTestId ? "".concat(dataTestId, "-prev") : undefined,
    disabled: current === 0,
    onClick: onPrev,
    shape: "square",
    size: "S"
  }, /*#__PURE__*/React.createElement(Chevron, {
    direction: "left"
  })), /*#__PURE__*/React.createElement(Spacer, {
    width: 4
  }), /*#__PURE__*/React.createElement(StyledIconButton, {
    "aria-label": strictLocalize({
      id: "9R+9Nz",
      defaultMessage: [{
        "type": 0,
        "value": "Volgende tip"
      }]
    }),
    className: "override",
    "data-test-id": dataTestId ? "".concat(dataTestId, "-next") : undefined,
    disabled: current === total - 1,
    onClick: onNext,
    shape: "square",
    size: "S",
    autoFocus: autoFocusNext
  }, /*#__PURE__*/React.createElement(Chevron, {
    direction: "right"
  }))));
};
var StyledIconButton = styled(IconButton).withConfig({
  displayName: "common__StyledIconButton"
})(["&.override:disabled > div,&.override[disabled] > div{background:", ";}"], Core.surface["default"]);
var toTooltipColors = function toTooltipColors(themeTooltip) {
  return {
    textColor: themeTooltip.color,
    backgroundColor: themeTooltip.background,
    borderColor: themeTooltip.border,
    actionButtons: themeTooltip.actionButtons
  };
};
export var mergeTooltipColors = function mergeTooltipColors(theme, variant, colors) {
  var _theme$tooltips, _theme$tooltips2;
  var darkVariantDefaults = {
    textColor: Core.text.inverse,
    backgroundColor: Core.surface["default"],
    borderColor: Core.border["default"]
  };
  var lightVariantDefaults = {
    textColor: Core.text["default"],
    backgroundColor: Core.surface.inverse,
    borderColor: Core.border.muted
  };
  var defaultColors = variant === "light" ? lightVariantDefaults : darkVariantDefaults;
  var themeColors = toTooltipColors(variant === "light" ? theme === null || theme === void 0 ? void 0 : (_theme$tooltips = theme.tooltips) === null || _theme$tooltips === void 0 ? void 0 : _theme$tooltips["default"] : theme === null || theme === void 0 ? void 0 : (_theme$tooltips2 = theme.tooltips) === null || _theme$tooltips2 === void 0 ? void 0 : _theme$tooltips2.primary);
  var borderFallback = {
    borderColor: colors === null || colors === void 0 ? void 0 : colors.backgroundColor
  };
  return merge({}, defaultColors, themeColors, borderFallback, colors);
};
export var getContents = function getContents(currentIndex, contents) {
  var _titles, _bodies;
  var titles;
  if (contents === undefined) {
    titles = undefined;
  } else if (Array.isArray(contents)) {
    titles = contents.map(function (c) {
      return c.title;
    });
  } else if (contents.bodies === undefined) {
    titles = [contents.title];
  } else {
    titles = Array(contents.bodies.length).fill(contents.title);
  }
  var bodies;
  if (contents === undefined) {
    bodies = undefined;
  } else if (Array.isArray(contents)) {
    bodies = contents.map(function (c) {
      return c.body;
    });
  } else {
    bodies = contents.bodies;
  }
  var numIndices = Math.max(((_titles = titles) === null || _titles === void 0 ? void 0 : _titles.length) || 0, ((_bodies = bodies) === null || _bodies === void 0 ? void 0 : _bodies.length) || 0);
  var currentTitle = titles ? titles[currentIndex] : undefined;
  var currentBody = bodies ? bodies[currentIndex] : undefined;
  return {
    currentTitle: currentTitle,
    currentBody: currentBody,
    numIndices: numIndices
  };
};
var CloseWrapper = styled(IconButton).withConfig({
  displayName: "common__CloseWrapper"
})(["flex-shrink:0;margin-block-start:-12px;"]);
var TextContainer = styled(Flex).attrs({
  direction: "row-reverse"
}).withConfig({
  displayName: "common__TextContainer"
})(["width:100%;"]);
var TextWrapper = styled(Column).attrs({
  align: "flex-start"
}).withConfig({
  displayName: "common__TextWrapper"
})(["flex-grow:1;"]);
export var _buildTooltip = function _buildTooltip(tooltipProps, dismissible) {
  var _ref15;
  var children = tooltipProps.children,
    _tooltipProps$positio = tooltipProps.position,
    position = _tooltipProps$positio === void 0 ? "top" : _tooltipProps$positio,
    _tooltipProps$width = tooltipProps.width,
    width = _tooltipProps$width === void 0 ? 336 : _tooltipProps$width,
    _tooltipProps$pointer = tooltipProps.pointerOffset,
    pointerOffset = _tooltipProps$pointer === void 0 ? 0 : _tooltipProps$pointer,
    className = tooltipProps.className,
    icon = tooltipProps.icon,
    iconColor = tooltipProps.iconColor,
    targetNode = tooltipProps.targetNode,
    innerClassName = tooltipProps.innerClassName,
    primary = tooltipProps.primary,
    variant = tooltipProps.variant,
    contents = tooltipProps.contents,
    action = tooltipProps.action,
    isOpen = tooltipProps.isOpen,
    _tooltipProps$hidden = tooltipProps.hidden,
    hidden = _tooltipProps$hidden === void 0 ? false : _tooltipProps$hidden,
    colors = tooltipProps.colors,
    popout = tooltipProps.popout,
    dataTestId = tooltipProps["data-test-id"],
    innerRef = tooltipProps.innerRef,
    styledComponentsTheme = tooltipProps.theme,
    onIndexChange = tooltipProps.onIndexChange,
    onDismiss = tooltipProps.onDismiss,
    _tooltipProps$keepIns = tooltipProps.keepInsideViewport,
    keepInsideViewport = _tooltipProps$keepIns === void 0 ? false : _tooltipProps$keepIns;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    currentIndex = _useState2[0],
    setCurrentIndex = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isOpenForDismissible = _useState4[0],
    setIsOpenForDismissible = _useState4[1];
  // track the hovered state to recalculate the offset when the tooltip is opened
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isHovered = _useState6[0],
    setIsHovered = _useState6[1];
  var effectiveIsOpen = dismissible ? isOpenForDismissible : isOpen;
  var theme = themeOrDefault(styledComponentsTheme);
  var tooltipVariant = getTooltipVariant(variant, primary);
  var handleClose = function handleClose(e) {
    setIsOpenForDismissible(false);
    if (onDismiss) {
      onDismiss(e);
    }
  };
  var _getContents = getContents(currentIndex, contents),
    currentTitle = _getContents.currentTitle,
    currentBody = _getContents.currentBody,
    numIndices = _getContents.numIndices;
  var mergedColors = mergeTooltipColors(theme, tooltipVariant, colors);
  var _useStrictLocalizatio2 = useStrictLocalization(),
    strictLocalize = _useStrictLocalizatio2.strictLocalize;
  var _useTooltipOffsetToKe = useTooltipOffsetToKeepTooltipInsideViewport(keepInsideViewport, position, pointerOffset, isHovered),
    offset = _useTooltipOffsetToKe.offset,
    tooltipBoxRef = _useTooltipOffsetToKe.tooltipBoxRef;
  return /*#__PURE__*/React.createElement(OuterWrapper, {
    className: className,
    "$dismissible": dismissible,
    "$isOpen": effectiveIsOpen,
    "$popout": !!popout,
    ref: innerRef,
    "$hidden": hidden,
    onMouseEnter: function onMouseEnter() {
      return setIsHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setIsHovered(false);
    },
    tabIndex: 0,
    onFocus: function onFocus() {
      return setIsHovered(true);
    },
    onBlur: function onBlur() {
      return setIsHovered(false);
    }
  }, (_ref15 = targetNode !== null && targetNode !== void 0 ? targetNode : icon) !== null && _ref15 !== void 0 ? _ref15 : /*#__PURE__*/React.createElement(Information, {
    fontSize: "16px",
    color: iconColor
  }), /*#__PURE__*/React.createElement(TooltipWrapper, {
    "$position": position,
    "$width": width,
    "$offset": offset,
    className: innerClassName,
    "$colors": mergedColors,
    role: "tooltip",
    "$popout": popout,
    "$tinyRightPadding": dismissible || numIndices > 1,
    "data-test-id": dataTestId,
    ref: tooltipBoxRef
  }, /*#__PURE__*/React.createElement(TextContainer, null, dismissible ? (/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CloseWrapper, {
    "aria-label": strictLocalize({
      id: "sqyFFG",
      defaultMessage: [{
        "type": 0,
        "value": "Sluiten"
      }]
    }),
    "data-test-id": dataTestId ? "".concat(dataTestId, "-close") : undefined,
    onClick: handleClose,
    shape: "square",
    size: "S"
  }, /*#__PURE__*/React.createElement(Close, null)), /*#__PURE__*/React.createElement(Spacer, {
    height: 4
  }))) : null, /*#__PURE__*/React.createElement(TextWrapper, null, currentTitle ? (/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TooltipText, {
    variant: "paragraphSansMedium"
  }, currentTitle), /*#__PURE__*/React.createElement(Spacer, {
    height: 4
  }))) : null, currentBody ? (/*#__PURE__*/React.createElement(TooltipText, {
    variant: "paragraphSansRegular"
  }, currentBody)) : null, children, /*#__PURE__*/React.createElement(Spacer, {
    height: 16
  }))), action ? (/*#__PURE__*/React.createElement(React.Fragment, null, action, /*#__PURE__*/React.createElement(Spacer, {
    height: 16
  }))) : null, numIndices > 1 ? (/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ActionButtons, {
    theme: theme,
    current: currentIndex,
    total: numIndices,
    onPrev: function onPrev() {
      var newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      onIndexChange && onIndexChange(newIndex);
    },
    onNext: function onNext() {
      var newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      onIndexChange && onIndexChange(newIndex);
    },
    colors: colors,
    variant: variant
  }), /*#__PURE__*/React.createElement(Spacer, {
    height: 11
  }))) : (/*#__PURE__*/React.createElement(Spacer, {
    height: 7
  })), /*#__PURE__*/React.createElement(Tip, {
    "$position": position,
    "$offset": offset
  }), /*#__PURE__*/React.createElement(SpaceBetweenIconAndTooltip, {
    "$position": position
  })));
};
export var _buildTinyTooltip = function _buildTinyTooltip(tinyTooltipProps) {
  var _ref16;
  var children = tinyTooltipProps.children,
    _tinyTooltipProps$pos = tinyTooltipProps.position,
    position = _tinyTooltipProps$pos === void 0 ? "top" : _tinyTooltipProps$pos,
    _tinyTooltipProps$poi = tinyTooltipProps.pointerOffset,
    pointerOffset = _tinyTooltipProps$poi === void 0 ? 0 : _tinyTooltipProps$poi,
    className = tinyTooltipProps.className,
    icon = tinyTooltipProps.icon,
    iconColor = tinyTooltipProps.iconColor,
    targetNode = tinyTooltipProps.targetNode,
    innerClassName = tinyTooltipProps.innerClassName,
    primary = tinyTooltipProps.primary,
    variant = tinyTooltipProps.variant,
    text = tinyTooltipProps.text,
    isOpen = tinyTooltipProps.isOpen,
    colors = tinyTooltipProps.colors,
    popout = tinyTooltipProps.popout,
    innerRef = tinyTooltipProps.innerRef,
    styledComponentsTheme = tinyTooltipProps.theme,
    _tinyTooltipProps$kee = tinyTooltipProps.keepInsideViewport,
    keepInsideViewport = _tinyTooltipProps$kee === void 0 ? false : _tinyTooltipProps$kee;
  var theme = themeOrDefault(styledComponentsTheme);
  // track the hovered state to recalculate the offset when the tooltip is opened
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isHovered = _useState8[0],
    setIsHovered = _useState8[1];
  var tooltipVariant = getTooltipVariant(variant, primary);
  var _useTooltipOffsetToKe2 = useTooltipOffsetToKeepTooltipInsideViewport(keepInsideViewport, position, pointerOffset, isHovered),
    offset = _useTooltipOffsetToKe2.offset,
    tooltipBoxRef = _useTooltipOffsetToKe2.tooltipBoxRef;
  return /*#__PURE__*/React.createElement(OuterWrapper, {
    className: className,
    "$isOpen": isOpen,
    "$popout": !!popout,
    ref: innerRef,
    onMouseEnter: function onMouseEnter() {
      return setIsHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setIsHovered(false);
    },
    tabIndex: 0,
    onFocus: function onFocus() {
      return setIsHovered(true);
    },
    onBlur: function onBlur() {
      return setIsHovered(false);
    }
  }, (_ref16 = targetNode !== null && targetNode !== void 0 ? targetNode : icon) !== null && _ref16 !== void 0 ? _ref16 : /*#__PURE__*/React.createElement(Information, {
    fontSize: "16px",
    color: iconColor
  }), /*#__PURE__*/React.createElement(TooltipWrapper, {
    "$position": position,
    "$width": "auto",
    "$offset": offset,
    "$tiny": true,
    className: innerClassName,
    "$colors": mergeTooltipColors(theme, tooltipVariant, colors),
    "$popout": popout,
    ref: tooltipBoxRef
  }, text ? /*#__PURE__*/React.createElement(TooltipText, {
    "$oneLiner": true
  }, text) : null, children, /*#__PURE__*/React.createElement(Tip, {
    "$position": position,
    "$offset": offset
  })));
};
var getTooltipVariant = function getTooltipVariant(variant, primary) {
  if (variant === undefined && primary === undefined) {
    return "dark";
  } else if (variant !== undefined) {
    return variant;
  } else {
    return primary ? "dark" : "light";
  }
};